import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';

export default function WebinarModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [showExperience, setShowExperience] = useState(false);
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
  
  const experienceLevel = watch('experience_level');

  const onSubmit = async (data) => {
    setLoading(true);
    
    try {
      // Get referral code from localStorage
      const referralCode = localStorage.getItem('referral_code');
      
      // Prepare payload
      const payload = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        ...(data.experience_level && { experience_level: data.experience_level }),
        ...(referralCode && { referral_code: referralCode }),
        source: '21-day-challenge-landing'
      };

      // Get backend URL from env (required for deployment)
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Track Lead event
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: '21-Day Challenge Registration',
          content_category: 'Trading Education'
        });
      }

      // Submit to API
      const response = await axios.post(`${backendUrl}/api/webinar/register`, payload);
      
      console.log('Registration successful:', response.data);
      
      // Track CompleteRegistration event
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_name: '21-Day Challenge',
          status: 'completed'
        });
      }

      // GTM event
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'webinar_success',
          registration_source: '21-day-challenge-landing'
        });
      }

      // Show success toast
      toast.success('Registration successful! Redirecting to login...');
      
      // Close modal
      onOpenChange(false);
      
      // Redirect to auth after 3 seconds
      setTimeout(() => {
        const authRedirect = encodeURIComponent(window.location.origin);
        window.location.href = `${window.location.origin}/login`;
      }, 3000);
      
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-white backdrop-blur-xl border border-slate-200 max-w-md shadow-xl"
        data-testid="webinar-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#162959]">
            Reserve Your Spot in the Challenge
          </DialogTitle>
          <DialogDescription className="text-sm text-[#162959]/70">
            Join the 21-Day Trading Habit Challenge. Start building execution discipline.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} data-testid="webinar-form" className="grid gap-4 mt-4">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-[#162959]">Full Name *</Label>
            <Input
              id="name"
              placeholder="John Doe"
              {...register('name', { required: 'Name is required' })}
              className="mt-1.5 bg-white border-slate-200 text-[#162959] placeholder:text-slate-400"
              data-testid="webinar-form-name-input"
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-[#162959]">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...register('email', { 
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className="mt-1.5 bg-white border-slate-200 text-[#162959] placeholder:text-slate-400"
              data-testid="webinar-form-email-input"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="phone" className="text-[#162959]">Phone *</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="+1 (555) 000-0000"
              {...register('phone', { required: 'Phone is required' })}
              className="mt-1.5 bg-white border-slate-200 text-[#162959] placeholder:text-slate-400"
              data-testid="webinar-form-phone-input"
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>
            )}
          </div>

          {/* Experience Level (Optional, Collapsible) */}
          <div className="border border-slate-200 rounded-lg p-3">
            <button
              type="button"
              onClick={() => setShowExperience(!showExperience)}
              className="flex items-center justify-between w-full text-sm text-[#162959]/70 hover:text-[#337AF7] transition-colors"
            >
              <span>Trading Experience (Optional)</span>
              {showExperience ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            
            {showExperience && (
              <div className="mt-3">
                <Select onValueChange={(value) => setValue('experience_level', value)} value={experienceLevel}>
                  <SelectTrigger 
                    className="bg-white border-slate-200 text-[#162959]"
                    data-testid="webinar-form-experience-select"
                  >
                    <SelectValue placeholder="Select your level" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="beginner" className="text-[#162959] hover:bg-slate-100">Beginner (0-1 year)</SelectItem>
                    <SelectItem value="intermediate" className="text-[#162959] hover:bg-slate-100">Intermediate (1-3 years)</SelectItem>
                    <SelectItem value="advanced" className="text-[#162959] hover:bg-slate-100">Advanced (3+ years)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-[#337AF7] text-white font-semibold px-6 py-3 shadow-lg hover:bg-[#2563eb] hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#337AF7]/50 transition-all duration-200"
            data-testid="webinar-form-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Reserve My Spot'
            )}
          </Button>

          <p className="text-xs text-center text-[#162959]/50">
            By registering, you agree to receive challenge updates via email.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}